import { useState } from 'react';
import clsx from 'clsx';
import type { OfferteRouteOption } from '../../lib/offertes';

interface Props {
  routes: OfferteRouteOption[];
  selectedId: string | null;
  onSelect: (routeId: string | null) => void;
  visibleCount: number;
  onShowMore: () => void;
  isLoading: boolean;
  error: string | null;
  onBumpCapacity?: (routeId: string) => void;
}

const STEP = 3;

export function OfferteRouteList({
  routes,
  selectedId,
  onSelect,
  visibleCount,
  onShowMore,
  isLoading,
  error,
  onBumpCapacity,
}: Props) {
  // Venster-dagen (gebied tijdelijk dicht) staan standaard niet in de lijst.
  // Zie RouteOptionList voor de reden.
  const [toonGesloten, setToonGesloten] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse" role="status" aria-label="Routes laden">
        <div className="h-12 bg-slate-100 rounded-md" />
        <div className="h-12 bg-slate-100 rounded-md" />
        <div className="h-12 bg-slate-100 rounded-md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
        {error}
      </div>
    );
  }

  const geslotenRoutes = routes.filter((r) => r.gebiedGesloten === true);
  const geslotenReden = geslotenRoutes.find((r) => r.gebiedGeslotenReden)?.gebiedGeslotenReden ?? null;
  const bruikbareRoutes = toonGesloten ? routes : routes.filter((r) => r.gebiedGesloten !== true);

  const visible = bruikbareRoutes.slice(0, visibleCount);
  const hiddenCount = Math.max(0, bruikbareRoutes.length - visibleCount);
  const nextStep = Math.min(STEP, hiddenCount);

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-slate-600 block">Ophaalmoment</label>

      {geslotenRoutes.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
          <span className="text-sm leading-5">⛔</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-red-900">
              Gebied dicht{geslotenReden ? `: ${geslotenReden}` : ''}
            </div>
            <div className="text-[11px] text-red-800">
              {geslotenRoutes.length} moment{geslotenRoutes.length === 1 ? '' : 'en'} in deze periode
              {toonGesloten ? ' staan hieronder tussen de opties.' : ' zijn verborgen.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setToonGesloten((v) => !v)}
            className="flex-shrink-0 text-[11px] font-medium px-2 py-1 border border-red-300 rounded bg-white text-red-800 hover:bg-red-100"
          >
            {toonGesloten ? 'Verbergen' : 'Toon toch'}
          </button>
        </div>
      )}

      {bruikbareRoutes.length === 0 && (
        <div className="px-3 py-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
          Geen beschikbare momenten gevonden.
        </div>
      )}

      <label
        className={clsx(
          'flex items-start gap-3 px-3 py-2 border rounded-md cursor-pointer transition-colors',
          selectedId === null
            ? 'border-indigo-500 bg-indigo-50'
            : 'border-slate-200 hover:border-slate-300 bg-white'
        )}
      >
        <input
          type="radio"
          name="offerte-route"
          className="mt-1 accent-indigo-600"
          checked={selectedId === null}
          onChange={() => onSelect(null)}
        />
        <div className="text-sm text-slate-700">Klant kiest zelf een moment (via mail-link)</div>
      </label>

      {visible.map((route) => {
        const isSelected = selectedId === route.routeId;
        const isFull = route.isFull === true;
        const isVandaag = route.isVandaag === true;
        const isNietVoorstellen = route.isNietVoorstellen === true;
        const isGebiedGesloten = route.gebiedGesloten === true;
        const isLowCapacity = !isFull && route.capaciteit > 0 && route.capaciteit <= 3;
        return (
          <label
            key={route.routeId}
            className={clsx(
              'flex items-start gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors',
              isSelected
                ? 'border-indigo-500 bg-indigo-50'
                : isVandaag
                  ? 'border-amber-300 bg-amber-50/60 hover:border-amber-400'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
            )}
          >
            <input
              type="radio"
              name="offerte-route"
              className="mt-1 accent-indigo-600"
              checked={isSelected}
              onChange={() => onSelect(route.routeId)}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">
                {route.dagNaam
                  ? `${route.dagNaam} ${route.dagNummer ?? ''} ${route.maandNaam ?? ''}`.trim()
                  : route.datum}
              </div>
              <div className="text-xs text-slate-500">{route.tijdslot}</div>
              {(isVandaag || isNietVoorstellen || isGebiedGesloten) && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {isVandaag && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                      🚐 Chauffeur onderweg
                    </span>
                  )}
                  {isGebiedGesloten && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                      ⛔ Gebied dicht{route.gebiedGeslotenReden ? `: ${route.gebiedGeslotenReden}` : ''}
                    </span>
                  )}
                  {isNietVoorstellen && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      Niet voorstellen
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {isFull ? (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                  Vol
                </span>
              ) : (
                <span
                  className={clsx(
                    'text-[10px] font-medium px-1.5 py-0.5 rounded',
                    isLowCapacity
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-emerald-50 text-emerald-700'
                  )}
                >
                  {route.capaciteit} plek{route.capaciteit === 1 ? '' : 'ken'}
                </span>
              )}
              {onBumpCapacity && (isFull || isLowCapacity) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onBumpCapacity(route.routeId);
                  }}
                  className={clsx(
                    'text-[10px] hover:underline',
                    isFull ? 'text-amber-700 font-medium' : 'text-indigo-600'
                  )}
                >
                  Capaciteit +
                </button>
              )}
            </div>
          </label>
        );
      })}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onShowMore}
          className="text-xs text-indigo-600 hover:underline"
        >
          Nog {nextStep} tonen{' '}
          <span className="text-slate-400">({hiddenCount} meer beschikbaar)</span>
        </button>
      )}
    </div>
  );
}
